import { Button, TextInput, Title, PasswordInput, Text, Box, Container, Paper } from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { IconUser, IconLock, IconPlus, IconEyeOff, IconEye } from '@tabler/icons-react';
import type { IFormularioAutenticacionVistaProps } from "../interfaces";

const FigurasFondoGeneral = ({ isMobile, isTablet }: { isMobile: boolean, isTablet: boolean }) => {
    // Usar tamaños responsivos
    return (
        <>
            {/* Círculo grande difuminado arriba izquierda */}
            <Box
                style={{
                    position: 'absolute',
                    left: isMobile ? '-60px' : isTablet ? '-120px' : '-180px',
                    top: isMobile ? '-40px' : isTablet ? '-80px' : '-120px',
                    width: isMobile ? '120px' : isTablet ? '240px' : '400px',
                    height: isMobile ? '120px' : isTablet ? '240px' : '400px',
                    borderRadius: '50%',
                    background: 'rgba(37, 99, 235, 0.18)',
                    zIndex: 0,
                }}
            />
            {/* Círculo grande difuminado abajo derecha */}
            <Box
                style={{
                    position: 'absolute',
                    right: isMobile ? '-50px' : isTablet ? '-100px' : '-160px',
                    bottom: isMobile ? '-40px' : isTablet ? '-80px' : '-120px',
                    width: isMobile ? '90px' : isTablet ? '180px' : '320px',
                    height: isMobile ? '90px' : isTablet ? '180px' : '320px',
                    borderRadius: '50%',
                    background: 'rgba(96, 165, 250, 0.4)',
                    zIndex: 0,
                }}
            />
            {/* Rectángulo diagonal difuminado */}
            <Box
                style={{
                    position: 'absolute',
                    left: isMobile ? '-30px' : isTablet ? '-50px' : '-80px',
                    top: isMobile ? '40%' : isTablet ? '30%' : '30%',
                    width: isMobile ? '180px' : isTablet ? '350px' : '600px',
                    height: isMobile ? '40px' : isTablet ? '80px' : '120px',
                    background: 'rgba(37, 100, 235, 0.55)',
                    transform: 'rotate(-18deg)',
                    borderRadius: isMobile ? '30px' : isTablet ? '60px' : '80px',
                    zIndex: 0,
                }}
            />
            {/* Círculo sólido semitransparente al centro */}
            <Box
                style={{
                    position: 'absolute',
                    left: isMobile ? '55%' : isTablet ? '45%' : '38%',
                    top: isMobile ? '65%' : isTablet ? '60%' : '55%',
                    width: isMobile ? '40px' : isTablet ? '70px' : '110px',
                    height: isMobile ? '40px' : isTablet ? '70px' : '110px',
                    borderRadius: '50%',
                    background: 'rgba(37, 99, 235, 0.10)',
                    zIndex: 0,
                }}
            />
            {/* Rectángulo horizontal difuminado al fondo */}
            <Box
                style={{
                    position: 'absolute',
                    left: isMobile ? '10%' : isTablet ? '15%' : '20%',
                    bottom: isMobile ? '4%' : isTablet ? '6%' : '8%',
                    width: isMobile ? '120px' : isTablet ? '220px' : '400px',
                    height: isMobile ? '20px' : isTablet ? '40px' : '60px',
                    background: 'rgba(96, 165, 250, 0.09)',
                    borderRadius: isMobile ? '16px' : isTablet ? '28px' : '40px',
                    filter: 'blur(8px)',
                    zIndex: 0,
                }}
            />
            {/* Líneas curvas suaves (SVG) */}
            <svg
                style={{
                    position: 'absolute',
                    left: isMobile ? '4%' : isTablet ? '6%' : '8%',
                    top: isMobile ? '10%' : isTablet ? '14%' : '18%',
                    width: isMobile ? '60px' : isTablet ? '120px' : '180px',
                    height: isMobile ? '40px' : isTablet ? '80px' : '120px',
                    zIndex: 0,
                }}
                viewBox="0 0 180 120"
                fill="none"
            >
                <path
                    d="M10,100 Q50,80 90,100 Q130,120 170,80"
                    stroke="rgba(37,99,235,0.10)"
                    strokeWidth={isMobile ? 1 : 2}
                    fill="none"
                />
                <path
                    d="M20,110 Q60,90 100,110 Q140,130 160,90"
                    stroke="rgba(96,165,250,0.10)"
                    strokeWidth={isMobile ? 1 : 1.5}
                    fill="none"
                />
            </svg>
        </>
    );
};

// Figuras DECORATIVAS para el panel izquierdo de la tarjeta
const FigurasPanelIzquierdo = ({ isMobile }: { isMobile: boolean }) => {
    if (isMobile) return null;
    return (
        <>
            {/* Triángulo oscuro */}
            <Box
                style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: '120px',
                    height: '120px',
                    background: 'rgba(60, 40, 120, 0.18)',
                    clipPath: 'polygon(100% 0, 0 0, 100% 100%)',
                    zIndex: 1,
                }}
            />
            {/* Líneas curvas blancas (SVG) */}
            <svg
                style={{
                    position: 'absolute',
                    right: '10%',
                    bottom: '10%',
                    width: '140px',
                    height: '90px',
                    zIndex: 2,
                }}
                viewBox="0 0 140 90"
                fill="none"
            >
                <path
                    d="M10,70 Q40,50 70,70 Q100,90 130,50"
                    stroke="white"
                    strokeOpacity="0.5"
                    strokeWidth="2"
                    fill="none"
                />
                <path
                    d="M20,80 Q50,60 90,80 Q120,100 130,60"
                    stroke="white"
                    strokeOpacity="0.3"
                    strokeWidth="1.5"
                    fill="none"
                />
            </svg>
            {/* Patrón de puntos */}
            <Box
                style={{
                    position: 'absolute',
                    right: '10%',
                    top: '15%',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gridTemplateRows: 'repeat(5, 1fr)',
                    gap: '6px',
                    width: 'fit-content',
                    zIndex: 2,
                }}
            >
                {[...Array(15)].map((_, i) => (
                    <Box
                        key={i}
                        style={{
                            width: '3px',
                            height: '3px',
                            background: 'rgba(255,255,255,0.7)',
                            borderRadius: '50%'
                        }}
                    />
                ))}
            </Box>
            {/* Círculos y signos más */}
            <Box
                style={{
                    position: 'absolute',
                    left: '18%',
                    top: '8%',
                    width: '14px',
                    height: '14px',
                    border: '2px solid rgba(255,255,255,0.5)',
                    borderRadius: '50%',
                    background: 'transparent',
                    zIndex: 2,
                }}
            />
            <Box
                style={{
                    position: 'absolute',
                    left: '38%',
                    top: '22%',
                    width: '14px',
                    height: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2,
                }}
            >
                <IconPlus size={10} color="rgba(255,255,255,0.5)" />
            </Box>
        </>
    );
};

// Componente principal del formulario
const FormularioAutenticacionVista: React.FC<IFormularioAutenticacionVistaProps> = ({
    iniciarSesion,
    inputsUsuario,
    alCambiarValor,
    cargando
}) => {
    const isMobile = useMediaQuery('(max-width: 768px)') ?? false;
    const isTablet = useMediaQuery('(max-width: 1024px)') ?? false;

    // Define tamaño Mantine según dispositivo
    const mantineSize = isMobile ? 'xs' : isTablet ? 'sm' : 'md';

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        iniciarSesion();
    };

    const [visible, { toggle }] = useDisclosure(false);

    return (
        <Box
            style={{
                minHeight: "100vh",
                width: "100vw",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                background: "linear-gradient(135deg, rgb(25 64 201), rgb(16 65 205))",
                fontFamily: "'Inter', sans-serif",
                padding: isMobile ? "1rem" : isTablet ? "1.5rem" : "2rem",
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            {/* Figuras sencillas del fondo general */}
            <FigurasFondoGeneral isMobile={isMobile} isTablet={isTablet} />

            <Paper
                radius={0}
                shadow="md"
                style={{
                    zIndex: 1, // <-- Asegura que el formulario esté sobre las figuras
                    display: "flex",
                    width: "100%",
                    maxWidth: isMobile ? "98vw" : isTablet ? "700px" : "900px",
                    minWidth: isMobile ? "90vw" : "400px",
                    overflow: "hidden",
                    backgroundColor: "transparent",
                    boxShadow: "none",
                    paperRadius: 'none',
                    flexDirection: isMobile ? "column" : "row",
                    flexWrap: isTablet ? "wrap" : "nowrap",
                    alignItems: isMobile ? "stretch" : "center",
                    minHeight: isMobile ? undefined : "420px",
                }}
            >
                {/* Lado izquierdo */}
                <Box
                    style={{
                        flex: 1,
                        background: "linear-gradient(135deg, #1450f5, #103ec3)",
                        color: "white",
                        padding: isMobile ? "1.2rem" : isTablet ? "2rem" : "3rem",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        position: "relative",
                        minHeight: isMobile ? "140px" : isTablet ? "220px" : "320px",
                        height: isMobile ? undefined : isTablet ? "260px" : "400px",
                        zIndex: 2,
                        boxShadow: isMobile ? undefined : "rgb(103 103 103 / 79%) 8px 0px 24px -8px",
                        marginRight: isMobile ? 0 : isTablet ? "-20px" : "-40px",
                        borderRadius: isMobile ? "12px 12px 0 0" : "12px 0 0 12px",
                        minWidth: isMobile ? "100%" : "50%",
                        maxWidth: isMobile ? "100%" : "50%",
                    }}
                >
                    {/* Figuras decorativas SOLO dentro del panel izquierdo */}
                    <FigurasPanelIzquierdo isMobile={isMobile} />

                    {/* SVG decorativo del ejemplo */}
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 200 200"
                        style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            opacity: 0.05,
                        }}
                    >
                        <path
                            d="M0,100 Q50,50 100,100 T200,100"
                            fill="none"
                            stroke="white"
                            strokeWidth="2"
                        />
                    </svg>

                    <Title order={1} style={{ fontWeight: 700, marginBottom: "1rem", fontSize: isMobile ? "1.3rem" : "2rem" }}>
                        ¡Bienvenido de vuelta!
                    </Title>
                    <Text
                        size={isMobile ? '0.9rem' : '1rem'}
                        fw={600}
                        c="white"
                        style={{
                            marginBottom: '1rem',
                            opacity: 0.9,
                            lineHeight: 1.3
                        }}
                    >
                        Smart Conekta
                    </Text>
                    <Text size={isMobile ? "sm" : "md"}>
                        Accede para conocer, gestionar y optimizar el mantenimiento de tus equipos.
                    </Text>
                </Box>

                {/* Lado derecho (formulario) */}
                <Box
                    style={{
                        flex: 1,
                        backgroundColor: "white",
                        padding: isMobile ? "1.2rem" : isTablet ? "2rem" : "3rem",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        borderRadius: isMobile ? "0 0 12px 12px" : "0 12px 12px 0",
                        minWidth: isMobile ? "100%" : "50%",
                        maxWidth: isMobile ? "100%" : "50%",
                    }}
                >
                    <Container size={420} w="100%">
                        <img
                            style={{
                                width: isMobile ? '190px' : isTablet ? '160px' : '240px', // más grande en mobile
                                display: 'block',
                                margin: '0 auto',
                                padding: isMobile ? '0.5rem' : '1rem'
                            }}
                            src={'./logo-conekta-smart-mesa-trabajo-1.png'}
                            alt="logo"
                        />
                        <Title
                            order={2}
                            style={{
                                color: "#333f49",
                                fontWeight: 700,
                                marginBottom: "1rem",
                                fontSize: isMobile ? "1.1rem" : "1.5rem"
                            }}
                        >
                            Iniciar Sesión
                        </Title>

                        <form onSubmit={handleSubmit} method="post" autoComplete="on">
                            <TextInput
                                label="Usuario"
                                name="username"
                                autoComplete="username"
                                placeholder="Usuario"
                                value={inputsUsuario.Usuario}
                                onChange={(e) => alCambiarValor("Usuario", e.target.value)}
                                required
                                size={mantineSize}
                                radius="md"
                                disabled={cargando}
                                mb="md"
                                leftSection={<IconUser size={16} color="#6c757d" />}
                                styles={{
                                    label: {
                                        color: "#495057",
                                        fontWeight: 500,
                                        marginBottom: "8px",
                                        fontSize: "14px",
                                    },
                                    input: {
                                        border: "2px solid #e9ecef",
                                        height:
                                            mantineSize === "xs"
                                                ? "36px"
                                                : mantineSize === "sm"
                                                    ? "42px"
                                                    : "48px",
                                        fontSize:
                                            mantineSize === "xs"
                                                ? "12px"
                                                : mantineSize === "sm"
                                                    ? "13px"
                                                    : "14px",
                                    },
                                }}
                            />
                            <PasswordInput
                                label="Contraseña"
                                name="password"
                                autoComplete="current-password"
                                placeholder="Tu contraseña"
                                value={inputsUsuario.Contrasena}
                                onChange={(e) => alCambiarValor("Contrasena", e.target.value)}
                                required
                                size={mantineSize}
                                radius="md"
                                mb="md"
                                visibilityToggleIcon={({ reveal }) =>
                                    reveal ? <IconEyeOff color="#6c757d" size={16} /> : <IconEye color="#6c757d" size={16} />
                                }
                                disabled={cargando}
                                leftSection={<IconLock size={16} color="#6c757d" />}
                                visible={visible}
                                onVisibilityChange={toggle}
                                styles={{
                                    label: {
                                        color: "#495057",
                                        fontWeight: 500,
                                        marginBottom: "8px",
                                        fontSize: "14px",
                                    },
                                    input: {
                                        border: "2px solid #e9ecef",
                                        height:
                                            mantineSize === "xs"
                                                ? "36px"
                                                : mantineSize === "sm"
                                                    ? "42px"
                                                    : "48px",
                                        fontSize:
                                            mantineSize === "xs"
                                                ? "12px"
                                                : mantineSize === "sm"
                                                    ? "13px"
                                                    : "14px",
                                    },
                                }}
                            />

                            <Button
                                type="submit"
                                fullWidth
                                size={mantineSize}
                                radius="md"
                                loading={cargando}
                                style={{
                                    backgroundColor: "var(--mantine-color-primary-0)",
                                    height:
                                        mantineSize === "xs"
                                            ? "36px"
                                            : mantineSize === "sm"
                                                ? "42px"
                                                : "48px",
                                    fontSize:
                                        mantineSize === "xs"
                                            ? "13px"
                                            : mantineSize === "sm"
                                                ? "15px"
                                                : "16px",
                                    fontWeight: 600,
                                    marginTop: "1rem",
                                }}
                            >
                                Ingresar
                            </Button>
                        </form>
                        <Title style={{
                            color: "#929292",
                            textAlign: "center",
                            marginTop: "1rem",
                        }} size="xs">{`${import.meta.env.VITE_APP_VERSION}`}
                        </Title>
                    </Container>
                </Box>
            </Paper>
        </Box>
    );
};

export default FormularioAutenticacionVista;